/**
 * router.js
 * ---------------------------------------------------------------
 * A deliberately tiny History-API router. Sprint 1 has exactly two
 * top-level destinations (the public landing page and the Ask Max
 * app), so pulling in a routing library isn't warranted yet. This
 * gives real, shareable URLs (/ and /ask) with no extra dependency.
 *
 * If future sprints add real routed depth (account, billing,
 * admin, conversation history by id), swap this for react-router
 * — nothing here is a trap against that; components just read
 * `path` from `useRoute()`.
 */

import { useEffect, useState, useCallback } from "react";

export function navigate(to, state) {
  window.history.pushState(state || {}, "", to);
  window.dispatchEvent(new PopStateEvent("popstate"));
  // A client-side route change doesn't get the browser's normal
  // "new page starts at the top" behaviour for free — without this,
  // navigating to a new route from partway down the previous one
  // (e.g. a footer link) lands the visitor in the middle of the new
  // page. Every call site here is a real cross-page navigation (see
  // router.js usage), so this is always the right behaviour.
  window.scrollTo(0, 0);
}

export function useRoute() {
  const [route, setRoute] = useState({
    path: window.location.pathname,
    state: window.history.state,
  });

  useEffect(() => {
    const onPop = () =>
      setRoute({ path: window.location.pathname, state: window.history.state });
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const go = useCallback((to, state) => navigate(to, state), []);

  return { path: route.path, state: route.state, go };
}
